import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminShell from "../AdminShell";
import ConfirmModal from "../ConfirmModal";
import { adminFetch } from "../adminApi";
import { useAdminToast } from "../AdminToast";
import "./admin-blackout.css";

/**
 * RAdminBlackoutDates — /admin/blackout-dates
 *
 * Redesigned Tour Blackout Dates screen (design-ref/admin/Blackout Dates.html)
 * with functionality replicated 1:1 from the legacy
 * frontend/src/pages/AdminBlackoutDatesPage.js:
 *   - auth guard on mount (adminFetch: missing/expired token or 401 →
 *     /admin/login); legacy 403-on-fetch → clear token + redirect preserved
 *   - GET /api/admin/blackout-dates on mount (loading spinner / error toast)
 *   - blackout type tiles Single / Date Range / Recurring — swap the form
 *     fields and keep is_recurring / recurring_day in sync
 *     (single|range → false/null, recurring → true/1)
 *   - "Which tours to block?" — All Tours vs specific-time checkboxes
 *     (10:00 → 10:00 AM, 14:00 → 2:00 PM, 16:00 → 4:00 PM); selecting
 *     All Tours clears blackout_times; picking a time drops out of
 *     all-times mode; "Please select at least one time" warning when
 *     specific mode has no time picked (legacy: warning only, not blocking)
 *   - single date input writes start_date AND end_date; range end date has
 *     min=start_date; date inputs required (browser validation, as legacy)
 *   - submit validation: recurring needs a day ("Please select a day of the
 *     week"); non-recurring needs a start date ("Please select start date")
 *   - POST /api/admin/blackout-dates with the exact legacy payload fallbacks:
 *     start_date || '2025-01-01', end_date || start_date || '2025-01-01',
 *     reason || 'Tour unavailable', blackout_times: allTimesBlocked ? null :
 *     selected times; error shows data.detail || 'Failed to create blackout
 *     date'; success resets the form + refetches
 *   - saving state disables the submit button and shows "Adding..."
 *   - delete: design confirm modal ("Remove this blackout?") replaces
 *     window.confirm → DELETE /api/admin/blackout-dates/{id}; success
 *     refetches; failure → 'Failed to delete blackout date'
 *   - list cards: recurring = "Every {Day}" + blue Recurring pill; one-off =
 *     formatted date or "start → end" (en-AU, weekday short); blocked-times
 *     pill amber ("10:00 AM, 2:00 PM") or red ("All tours blocked"); reason;
 *     "Added by {created_by}"; per-card Delete
 *   - legacy inline success/error banners (3s auto-clear) → toast pills
 *   - legacy "Back to Dashboard" button is superseded by the shell sidebar
 */

const TOUR_TIMES = [
  { value: "10:00", label: "10:00 AM" },
  { value: "14:00", label: "2:00 PM" },
  { value: "16:00", label: "4:00 PM" },
];

// Legacy day mapping: 0 = Monday … 6 = Sunday
const DAY_NAMES = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const emptyBlackout = (type) => ({
  start_date: "",
  end_date: "",
  reason: "",
  is_recurring: type === "recurring",
  recurring_day: type === "recurring" ? 1 : null,
  blackout_times: [], // empty array = all times, specific times = only those
});

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString("en-AU", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const isDateRangeSame = (start, end) => start === end;

const getDayName = (dayNumber) => DAY_NAMES[dayNumber] || "";

const RAdminBlackoutDates = () => {
  const navigate = useNavigate();
  const toast = useAdminToast();

  const [blackoutDates, setBlackoutDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [blackoutType, setBlackoutType] = useState("single"); // 'single' | 'range' | 'recurring'
  const [newBlackout, setNewBlackout] = useState(emptyBlackout("single"));
  // true = block all tours, false = specific times only
  const [allTimesBlocked, setAllTimesBlocked] = useState(true);

  // Delete confirm state (design's confirm modal replaces window.confirm)
  const [deletingBlackout, setDeletingBlackout] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const fetchBlackoutDates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminFetch("/api/admin/blackout-dates", {
        navigate,
      });

      // Legacy parity: 403 is treated like an expired session too
      // (adminFetch already handles 401).
      if (response.status === 403) {
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminTokenExpires");
        navigate("/admin/login");
        return;
      }

      if (!response.ok) throw new Error("Failed to fetch blackout dates");

      const data = await response.json();
      setBlackoutDates(data);
    } catch (err) {
      if (!err.isAuthError) {
        console.error("Error fetching blackout dates:", err);
        toast.error("Failed to load blackout dates");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, toast]);

  useEffect(() => {
    fetchBlackoutDates();
  }, [fetchBlackoutDates]);

  const selectType = (type) => {
    setBlackoutType(type);
    setNewBlackout((prev) => ({
      ...prev,
      is_recurring: type === "recurring",
      recurring_day: type === "recurring" ? 1 : null,
    }));
  };

  const handleAllToursChange = (checked) => {
    if (checked) {
      setAllTimesBlocked(true);
      setNewBlackout((prev) => ({ ...prev, blackout_times: [] }));
    } else {
      setAllTimesBlocked(false);
    }
  };

  const handleTimeToggle = (value, checked) => {
    if (checked) {
      // picking a specific time drops out of all-times mode
      setAllTimesBlocked(false);
      setNewBlackout((prev) => ({
        ...prev,
        blackout_times: [...prev.blackout_times, value],
      }));
    } else {
      setNewBlackout((prev) => ({
        ...prev,
        blackout_times: prev.blackout_times.filter((t) => t !== value),
      }));
    }
  };

  const handleAddBlackout = async (e) => {
    e.preventDefault();

    // Validation (legacy copy)
    if (newBlackout.is_recurring) {
      if (newBlackout.recurring_day === null) {
        toast.error("Please select a day of the week");
        return;
      }
    } else if (!newBlackout.start_date) {
      toast.error("Please select start date");
      return;
    }

    try {
      setSaving(true);

      // Prepare payload — end_date defaults to start_date for single-day
      // blackouts, dummy date for recurring (exact legacy fallbacks).
      const payload = {
        start_date: newBlackout.start_date || "2025-01-01",
        end_date:
          newBlackout.end_date || newBlackout.start_date || "2025-01-01",
        reason: newBlackout.reason || "Tour unavailable",
        is_recurring: newBlackout.is_recurring,
        recurring_day: newBlackout.recurring_day,
        blackout_times: allTimesBlocked ? null : newBlackout.blackout_times,
      };

      const response = await adminFetch("/api/admin/blackout-dates", {
        method: "POST",
        body: JSON.stringify(payload),
        navigate,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to create blackout date");
      }

      toast.success("✓ Blackout date added");
      setNewBlackout(emptyBlackout(blackoutType));
      setAllTimesBlocked(true);
      fetchBlackoutDates();
    } catch (err) {
      if (!err.isAuthError) {
        toast.error(err.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deletingBlackout) return;
    try {
      setDeleteBusy(true);
      const response = await adminFetch(
        `/api/admin/blackout-dates/${deletingBlackout.id}`,
        { method: "DELETE", navigate }
      );

      if (!response.ok) throw new Error("Failed to delete blackout date");

      toast.success("Blackout removed");
      fetchBlackoutDates();
    } catch (err) {
      if (!err.isAuthError) {
        toast.error("Failed to delete blackout date");
      }
    } finally {
      setDeleteBusy(false);
      setDeletingBlackout(null);
    }
  };

  const timesLabel = (times) =>
    times
      .map((t) => {
        const time = TOUR_TIMES.find((tt) => tt.value === t);
        return time ? time.label : t;
      })
      .join(", ");

  return (
    <AdminShell
      active="blackout-dates"
      pageClass="pg-admin-blackout"
      eyebrow="Tours"
      title="Tour Blackout Dates"
      subtitle="Manage dates when online tour bookings are unavailable"
    >
      <div className="bo-layout">
        {/* ---- Add blackout date (left card) ---- */}
        <form className="adm-card bo-form" onSubmit={handleAddBlackout}>
          <div className="bo-form-title">Add blackout date</div>

          {/* type tiles — swap the form fields */}
          <div className="bo-tiles">
            <button
              type="button"
              className={
                "bo-tile" + (blackoutType === "single" ? " is-active" : "")
              }
              onClick={() => selectType("single")}
            >
              Single
            </button>
            <button
              type="button"
              className={
                "bo-tile" + (blackoutType === "range" ? " is-active" : "")
              }
              onClick={() => selectType("range")}
            >
              Date Range
            </button>
            <button
              type="button"
              className={
                "bo-tile" + (blackoutType === "recurring" ? " is-active" : "")
              }
              onClick={() => selectType("recurring")}
            >
              Recurring
            </button>
          </div>

          {blackoutType === "single" && (
            <label className="adm-field">
              <span className="adm-label">Date</span>
              <input
                type="date"
                className="adm-input"
                value={newBlackout.start_date}
                onChange={(e) =>
                  setNewBlackout((prev) => ({
                    ...prev,
                    start_date: e.target.value,
                    // Automatically set end date to same as start
                    end_date: e.target.value,
                  }))
                }
                required
                data-testid="single-date-input"
              />
            </label>
          )}

          {blackoutType === "range" && (
            <div className="bo-range">
              <label className="adm-field">
                <span className="adm-label">Start Date</span>
                <input
                  type="date"
                  className="adm-input"
                  value={newBlackout.start_date}
                  onChange={(e) =>
                    setNewBlackout((prev) => ({
                      ...prev,
                      start_date: e.target.value,
                    }))
                  }
                  required
                  data-testid="start-date-input"
                />
              </label>
              <label className="adm-field">
                <span className="adm-label">End Date</span>
                <input
                  type="date"
                  className="adm-input"
                  value={newBlackout.end_date}
                  onChange={(e) =>
                    setNewBlackout((prev) => ({
                      ...prev,
                      end_date: e.target.value,
                    }))
                  }
                  min={newBlackout.start_date}
                  required
                  data-testid="end-date-input"
                />
              </label>
            </div>
          )}

          {blackoutType === "recurring" && (
            <>
              <label className="adm-field">
                <span className="adm-label">Repeats every</span>
                <select
                  className="adm-select"
                  value={newBlackout.recurring_day ?? 1}
                  onChange={(e) =>
                    setNewBlackout((prev) => ({
                      ...prev,
                      recurring_day: parseInt(e.target.value),
                    }))
                  }
                  data-testid="recurring-day-select"
                >
                  <option value="0">Monday</option>
                  <option value="1">Tuesday</option>
                  <option value="2">Wednesday</option>
                  <option value="3">Thursday</option>
                  <option value="4">Friday</option>
                  <option value="5">Saturday</option>
                  <option value="6">Sunday</option>
                </select>
              </label>
              <p className="bo-hint">
                ℹ️ This will block online bookings every week on this day
              </p>
            </>
          )}

          <div className="adm-label bo-times-label">Which tours to block?</div>
          <div className="bo-times">
            <label className="bo-time">
              <input
                type="checkbox"
                className="adm-check"
                checked={allTimesBlocked}
                onChange={(e) => handleAllToursChange(e.target.checked)}
              />{" "}
              All Tours
            </label>
            {TOUR_TIMES.map((time) => (
              <label key={time.value} className="bo-time">
                <input
                  type="checkbox"
                  className="adm-check"
                  checked={
                    !allTimesBlocked &&
                    newBlackout.blackout_times.includes(time.value)
                  }
                  onChange={(e) =>
                    handleTimeToggle(time.value, e.target.checked)
                  }
                />{" "}
                {time.label}
              </label>
            ))}
          </div>
          {!allTimesBlocked && newBlackout.blackout_times.length === 0 && (
            <p className="bo-warn">⚠️ Please select at least one time</p>
          )}

          <label className="adm-field bo-reason">
            <span className="adm-label">Reason</span>
            <input
              className="adm-input"
              placeholder="e.g. Public holiday"
              value={newBlackout.reason}
              onChange={(e) =>
                setNewBlackout((prev) => ({ ...prev, reason: e.target.value }))
              }
              data-testid="reason-input"
            />
          </label>

          <button
            type="submit"
            className="adm-btn"
            disabled={saving}
            data-testid="add-blackout-btn"
          >
            {saving ? "Adding..." : "Add Blackout Date"}
          </button>
        </form>

        {/* ---- Current blackout dates (right column) ---- */}
        <div className="bo-list">
          {loading ? (
            <div className="adm-loading">
              <div className="adm-spinner" />
              Loading blackout dates…
            </div>
          ) : blackoutDates.length === 0 ? (
            <div className="adm-empty">
              <div className="adm-empty-icon">📅</div>
              <div className="adm-empty-title">
                No blackout dates configured
              </div>
            </div>
          ) : (
            blackoutDates.map((blackout) => (
              <div
                key={blackout.id}
                className="bo-item"
                data-testid={`blackout-${blackout.id}`}
              >
                <div className="bo-item-row">
                  <div className="bo-item-info">
                    <div className="bo-item-head">
                      <span className="bo-item-title">
                        {blackout.is_recurring
                          ? `Every ${getDayName(blackout.recurring_day)}`
                          : isDateRangeSame(
                                blackout.start_date,
                                blackout.end_date
                              )
                            ? formatDate(blackout.start_date)
                            : `${formatDate(blackout.start_date)} → ${formatDate(blackout.end_date)}`}
                      </span>
                      {blackout.is_recurring && (
                        <span className="bo-pill bo-pill--blue">Recurring</span>
                      )}
                    </div>
                    {/* which times are blocked */}
                    <div className="bo-item-times">
                      {blackout.blackout_times &&
                      blackout.blackout_times.length > 0 ? (
                        <span className="bo-pill bo-pill--amber">
                          {timesLabel(blackout.blackout_times)}
                        </span>
                      ) : (
                        <span className="bo-pill bo-pill--red">
                          All tours blocked
                        </span>
                      )}
                    </div>
                    {blackout.reason && (
                      <div className="bo-item-reason">{blackout.reason}</div>
                    )}
                    <div className="bo-item-by">
                      Added by {blackout.created_by}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="bo-del"
                    onClick={() => setDeletingBlackout(blackout)}
                    data-testid={`delete-blackout-${blackout.id}`}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Delete confirm (design pattern; same DELETE call as legacy) */}
      <ConfirmModal
        open={!!deletingBlackout}
        title="Remove this blackout?"
        body="Tours will become bookable again for these dates."
        confirmLabel="Remove"
        danger
        busy={deleteBusy}
        onConfirm={confirmDelete}
        onCancel={() => setDeletingBlackout(null)}
      />
    </AdminShell>
  );
};

export default RAdminBlackoutDates;
